import React, { useState, useCallback } from 'react';
import cl from 'classnames';

import { IconsEnum } from '../../../../../assets/icons/types';
import {
  ID,
  I18nText,
  FetchItemsFilterItems,
  FetchItemsSort,
  FetchItemsSortableField,
  FetchItemsSortItems,
  ClassName,
  GADataType
} from '../../../../../types';
import { CompactIndexTableHeaderSortFilterSearchCellFilterField } from './CompactIndexTableHeaderSortFilterSearchCell.types';

import { useCompactIndexTableHeaderSortFilterCell } from '../CompactIndexTableHeaderSortFilterCell/hooks/useCompactIndexTableHeaderSortFilterCell';

import { CompactIndexTableHeaderSortFilterCellPopover } from '../CompactIndexTableHeaderSortFilterCell/components/CompactIndexTableHeaderSortFilterCellPopover';

import { Translate } from '../../../../../helpers/Translate';
import { Icon } from '../../../../../helpers/Icon';
import { QuickSearchInputHelper } from '../../../../../helpers/QuickSearchInputHelper';
import { RefButtonHelper } from '../../../../../helpers/buttons/RefButtonHelper';

import { generateNanoId } from '../../../../../utils/generateNanoId';

interface CompactIndexTableHeaderSortFilterSearchCellProps<FiltersType> {
  viewId: ID;
  filterFields: CompactIndexTableHeaderSortFilterSearchCellFilterField<FiltersType>[];
  currentFilters?: FiltersType;
  currentSort?: FetchItemsSort;
  i18nText?: I18nText;
  onFilter: FetchItemsFilterItems<Partial<FiltersType>>;
  onSort?: FetchItemsSortItems;
  sortableField?: FetchItemsSortableField;
  searchField: keyof FiltersType;
  ascField?: string;
  descField?: string;
  className?: ClassName;
}

function CompactIndexTableHeaderSortFilterSearchCell<
  FiltersType extends Record<string, any>
>({
  ascField,
  className,
  currentFilters,
  currentSort,
  dataGa,
  descField,
  filterFields,
  i18nText,
  onFilter,
  onSort,
  searchField,
  sortableField,
  viewId
}: CompactIndexTableHeaderSortFilterSearchCellProps<FiltersType> & GADataType) {
  const withSort = Boolean(
    ((ascField && descField) || sortableField) && onSort
  );

  const [quickSearchInputKey, setQuickSearchInputKey] = useState<string>(() =>
    generateNanoId()
  );

  const handleAfterClear = useCallback<() => void>(() => {
    setQuickSearchInputKey(generateNanoId());
  }, []);

  const {
    ascSort,
    descSort,
    isSortedAsc,
    isSortedDesc,
    sortIcon,
    isFiltered,
    localFiltersState,
    setLocalFiltersState,
    isPopoverOpen,
    referencePopoverElement,
    referencePopoverCallbackRef,
    togglePopover,
    closePopover,
    handleApply,
    handleClear
  } = useCompactIndexTableHeaderSortFilterCell({
    viewId,
    filterFields,
    currentFilters,
    currentSort,
    afterClear: handleAfterClear,
    onFilter,
    sortableField,
    ascField,
    descField
  });

  return (
    <th
      scope="col"
      className={
        className ||
        'relative h-0 p-0 hover:bg-gray-100 dark:hover:bg-gray-800 w-full dark:border-gray-700 border-gray-200 border-b'
      }
    >
      <div className="flex h-full justify-stretch">
        <div className="group relative flex h-full py-2 px-2">
          <div className="flex items-center justify-center">
            <RefButtonHelper
              className="w-full focus:ring-0 focus:ring-offset-0"
              onClick={togglePopover}
              ref={referencePopoverCallbackRef}
            >
              <div
                className="absolute inset-0 hover:shadow dark:hover:glow z-5"
                aria-hidden="true"
              />
              <div
                className="absolute inset-0 border-2 border-current opacity-0 group-focus-within:opacity-100"
                aria-hidden="true"
              />

              <div className="flex gap-1 items-center text-gray-500 justify-start">
                {i18nText ? (
                  <span className="text-xs font-medium uppercase tracking-wider whitespace-nowrap">
                    <Translate id={i18nText} />
                  </span>
                ) : null}

                <div className="flex empty:hidden -mr-1">
                  {isSortedAsc || isSortedDesc ? (
                    <Icon className="w-4 h-4" icon={sortIcon} />
                  ) : null}
                  <Icon
                    className={cl(
                      'w-4 h-4',
                      isFiltered
                        ? 'text-blue-500'
                        : 'text-gray-400 dark:text-gray-600'
                    )}
                    icon={IconsEnum.FILTER_SOLID}
                  />
                </div>
              </div>
            </RefButtonHelper>

            {isPopoverOpen ? (
              <CompactIndexTableHeaderSortFilterCellPopover
                ascSort={ascSort}
                closePopover={closePopover}
                currentSort={currentSort}
                dataGa={dataGa}
                descSort={descSort}
                filterFields={filterFields}
                localFiltersState={localFiltersState}
                onApply={handleApply}
                onClear={handleClear}
                onSort={onSort}
                referencePopoverElement={referencePopoverElement}
                setLocalFiltersState={setLocalFiltersState}
                viewId={viewId}
                withSort={withSort}
              />
            ) : null}
          </div>
        </div>

        <QuickSearchInputHelper
          dataGa={dataGa}
          key={quickSearchInputKey}
          searchField={searchField}
          currentFilters={currentFilters}
          onFilter={onFilter}
        />
      </div>
    </th>
  );
}

export default CompactIndexTableHeaderSortFilterSearchCell;
